import type { AxiosError, AxiosInstance } from 'axios';
import axios, { isAxiosError } from 'axios';

export interface LotusValidationErrorT {
  loc: unknown[];
  msg: string;
  type: string;
}

export interface LotusErrorDetailT {
  message: string;
  code: string | null;
  payload: Record<string, unknown> | null;
  validationErrors: LotusValidationErrorT[] | null;
  status: number;
}

const LOTUS_ERROR_KEY = Symbol('lotusErrorDetail');

interface AxiosErrorWithLotusDetail extends AxiosError {
  [LOTUS_ERROR_KEY]?: LotusErrorDetailT;
}

/**
 * Parse a backend error response body into a structured LotusErrorDetailT.
 *
 * Handles:
 *  - RepresentedExceptionT: { message, trace, code?, payload?, status_code? }
 *  - FastAPI HTTPException: { detail: string }
 *  - Pydantic validation:   { detail: ValidationError[] }
 *
 * Returns null when the body is not a recognizable error shape.
 */
export function parseLotusErrorBody(
  data: unknown,
  status: number
): LotusErrorDetailT | null {
  if (data == null) {
    return null;
  }

  // Bare string response (e.g. plain-text error body)
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (trimmed.length === 0) return null;
    return {
      message: trimmed,
      code: null,
      payload: null,
      validationErrors: null,
      status,
    };
  }

  if (typeof data !== 'object') {
    return null;
  }

  const record = data as Record<string, unknown>;

  // RepresentedExceptionT format: has `message` field (and optionally `trace`)
  if (typeof record['message'] === 'string' && 'trace' in record) {
    const message = record['message'];
    const code =
      typeof record['code'] === 'string' ? record['code'] : null;
    const payload =
      record['payload'] != null && typeof record['payload'] === 'object'
        ? (record['payload'] as Record<string, unknown>)
        : null;

    // "an error occurred" is the generic production 500 placeholder — not useful
    if (message === 'an error occurred') {
      return {
        message: `Server error (${String(status)})`,
        code,
        payload,
        validationErrors: null,
        status,
      };
    }

    return { message, code, payload, validationErrors: null, status };
  }

  // FastAPI detail format
  if ('detail' in record) {
    // String detail (HTTPException / DoseSpotException)
    if (typeof record['detail'] === 'string' && record['detail'].trim() !== '') {
      return {
        message: record['detail'],
        code: null,
        payload: null,
        validationErrors: null,
        status,
      };
    }

    // Array detail (Pydantic RequestValidationError)
    if (Array.isArray(record['detail']) && record['detail'].length > 0) {
      const errors = record['detail'] as LotusValidationErrorT[];
      const firstMsg =
        typeof errors[0]?.msg === 'string' ? errors[0].msg : null;

      const summary =
        errors.length === 1
          ? (firstMsg ?? 'Validation error')
          : `${String(errors.length)} validation errors`;

      return {
        message: summary,
        code: null,
        payload: null,
        validationErrors: errors,
        status,
      };
    }
  }

  // Non-RepresentedExceptionT body with just a `message` field (e.g. 401, 540)
  if (typeof record['message'] === 'string' && record['message'].trim() !== '') {
    return {
      message: record['message'],
      code: null,
      payload: null,
      validationErrors: null,
      status,
    };
  }

  return null;
}

/**
 * Retrieve the parsed LotusErrorDetailT from an error, if it was enriched by
 * the interceptor.
 */
export function getLotusErrorDetail(error: unknown): LotusErrorDetailT | null {
  if (isAxiosError(error)) {
    return (error as AxiosErrorWithLotusDetail)[LOTUS_ERROR_KEY] ?? null;
  }
  return null;
}

/**
 * Create an axios response error interceptor that enriches AxiosErrors
 * in-place with parsed backend error information.
 *
 * After installation:
 * - `error.message` reflects the real backend error message
 * - `getLotusErrorDetail(error)` returns structured error metadata
 * - `isAxiosError(error)` remains true (the same object is re-thrown)
 */
export function createLotusErrorInterceptor(): (error: unknown) => Promise<never> {
  return (error: unknown) => {
    if (isAxiosError(error) && error.response) {
      const parsed = parseLotusErrorBody(
        error.response.data,
        error.response.status
      );

      if (parsed) {
        error.message = parsed.message;
        (error as AxiosErrorWithLotusDetail)[LOTUS_ERROR_KEY] = parsed;
      }
    }

    return Promise.reject(error);
  };
}

const INSTALLED_KEY = Symbol.for('lotushealth.errorInterceptorInstalled');

/**
 * Install the Lotus error interceptor on an axios instance (or the global
 * default). Safe to call multiple times (e.g. during Vite HMR) — only the
 * first call per instance actually registers the interceptor.
 */
export function installLotusErrorInterceptor(
  instance?: AxiosInstance
): void {
  const target = instance ?? axios;
  if ((target as unknown as Record<symbol, unknown>)[INSTALLED_KEY]) return;
  (target as unknown as Record<symbol, unknown>)[INSTALLED_KEY] = true;
  target.interceptors.response.use(undefined, createLotusErrorInterceptor());
}
